import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fmtDateShort, fmtTime } from '../utils/time';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function Patrols() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [patrols, setPatrols]       = useState([]);
  const [routes,  setRoutes]        = useState([]);
  const [loading, setLoading]       = useState(true);
  const [starting, setStarting]     = useState(null); // route id being started
  const [activePatrol, setActivePatrol] = useState(null); // current active patrol for guard
  const [filter, setFilter]         = useState('all'); // all | active | completed | abandoned

  const isGuard = user?.role === 'guard';
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  // ── Load patrols ────────────────────────────────────────────
  const loadPatrols = useCallback(() => {
    setLoading(true);
    axios.get('/api/patrols').then(r => {
      setPatrols(r.data);
      // Find any active patrol for the guard
      if (isGuard) {
        setActivePatrol(r.data.find(p => p.status === 'active') || null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [isGuard]);

  // ── Load routes (for guard view) ────────────────────────────
  const loadRoutes = useCallback(() => {
    if (!isGuard) return;
    axios.get('/api/routes').then(r => {
      setRoutes(r.data.filter(rt => rt.is_active));
    }).catch(() => {});
  }, [isGuard]);

  useEffect(() => {
    loadPatrols();
    loadRoutes();
  }, [loadPatrols, loadRoutes]);

  // ── Start patrol (guard) ────────────────────────────────────
  const handleStart = async (routeId) => {
    if (activePatrol) {
      toast.error('You already have an active patrol. Complete it first.');
      return;
    }
    setStarting(routeId);
    try {
      const res = await axios.post('/api/patrols/start', { route_id: routeId });
      toast.success('Patrol started! Good luck 👮');
      navigate(`/patrols/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to start patrol';
      toast.error(msg);
      if (msg.includes('already have')) loadPatrols(); // refresh to get active
    } finally {
      setStarting(null);
    }
  };

  // ── Stop patrol (admin/supervisor) ──────────────────────────
  const handleStop = async (patrolId) => {
    const reason = window.prompt('Reason for stopping this patrol:');
    if (!reason) return;
    try {
      await axios.post(`/api/patrols/${patrolId}/stop`, { reason });
      toast.success('Patrol stopped');
      loadPatrols();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop patrol');
    }
  };

  const statusStyle = {
    active:    { bg: '#fef3c7', color: '#92400e' },
    completed: { bg: '#dcfce7', color: '#166534' },
    abandoned: { bg: '#fee2e2', color: '#991b1b' },
  };

  const filtered = patrols.filter(p => filter === 'all' || p.status === filter);

  // ─────────────────────────────────────────────────────────────
  // GUARD VIEW
  // ─────────────────────────────────────────────────────────────
  if (isGuard) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-title">🚶 My Patrols</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
              Welcome, {user?.name?.split(' ')[0]}
            </div>
          </div>
        </div>

        {/* ── Active patrol banner ─────────────────────────── */}
        {activePatrol && (
          <div style={{
            background: 'linear-gradient(135deg,#f59e0b,#d97706)',
            borderRadius: 14, padding: '16px 20px',
            marginBottom: 20, color: 'white',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12,
            boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>🔄 Patrol In Progress</div>
              <div style={{ fontSize: 13, opacity: .85, marginTop: 3 }}>
                {activePatrol.route_name} · {activePatrol.site_name}
              </div>
              <div style={{ fontSize: 12, opacity: .7, marginTop: 2 }}>
                Started {fmtDateShort(activePatrol.started_at)}
              </div>
            </div>
            <Link to={`/patrols/${activePatrol.id}`} style={{
              background: 'white', color: '#d97706',
              padding: '9px 20px', borderRadius: 10,
              fontWeight: 700, fontSize: 13, textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}>
              Continue →
            </Link>
          </div>
        )}

        {/* ── Available routes ─────────────────────────────── */}
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 12 }}>
          📍 Available Patrol Routes
        </div>

        {routes.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🗺️</div>
            <div style={{ fontWeight: 600 }}>No routes available</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Ask your admin to assign a patrol route</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14, marginBottom: 26 }}>
            {routes.map(rt => (
              <div key={rt.id} style={{
                background: 'white', borderRadius: 16, padding: '18px 18px 16px',
                border: '1px solid #e8ecf4', boxShadow: '0 2px 8px rgba(0,0,0,.05)',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {/* Route header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                      📍 {rt.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      🏢 {rt.site_name}
                    </div>
                  </div>
                  <div style={{
                    background: '#eef2ff', color: '#4361ee',
                    fontSize: 11, fontWeight: 700, borderRadius: 8,
                    padding: '3px 9px',
                  }}>
                    {rt.color ? <span style={{ color: rt.color }}>●</span> : null} Active
                  </div>
                </div>

                {/* Stats row */}
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#4361ee' }}>
                      {rt.checkpoint_count || 0}
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                      Checkpoints
                    </div>
                  </div>
                  <div style={{ width: 1, background: '#f0f2f5' }} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#8b5cf6' }}>
                      {rt.expected_duration_minutes || 60}m
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                      Est. Duration
                    </div>
                  </div>
                  {rt.description && (
                    <>
                      <div style={{ width: 1, background: '#f0f2f5' }} />
                      <div style={{ flex: 1, fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
                        {rt.description}
                      </div>
                    </>
                  )}
                </div>

                {/* Start button */}
                <button
                  onClick={() => handleStart(rt.id)}
                  disabled={!!activePatrol || starting === rt.id}
                  style={{
                    padding: '11px 0', borderRadius: 10, border: 'none',
                    background: activePatrol
                      ? '#f1f5f9'
                      : 'linear-gradient(135deg,#4361ee,#7b8ff8)',
                    color: activePatrol ? '#94a3b8' : 'white',
                    fontWeight: 700, fontSize: 14, cursor: activePatrol ? 'not-allowed' : 'pointer',
                    transition: 'all .2s', boxShadow: activePatrol ? 'none' : '0 4px 12px rgba(67,97,238,0.3)',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {starting === rt.id
                    ? '⏳ Starting…'
                    : activePatrol
                    ? '⛔ Patrol Already Active'
                    : '▶ Start Patrol'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── My patrol history ─────────────────────────────── */}
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', marginBottom: 12 }}>
          📋 My Patrol History
        </div>
        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>Loading…</div>
          ) : patrols.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
              No patrols yet — start your first one above!
            </div>
          ) : (
            <div className="table-scroll">
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Route</th><th>Site</th><th>Started</th>
                    <th>Status</th><th>Compliance</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {patrols.map(p => {
                    const ss = statusStyle[p.status] || { bg: '#f3f4f6', color: '#374151' };
                    const c  = parseFloat(p.compliance_percent) || 0;
                    return (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>{p.route_name}</td>
                        <td style={{ color: '#64748b' }}>{p.site_name}</td>
                        <td style={{ fontSize: 12, color: '#94a3b8' }}>
                          {fmtDateShort(p.started_at)}
                        </td>
                        <td>
                          <span style={{
                            fontSize: 11, padding: '3px 10px', borderRadius: 20,
                            fontWeight: 700, background: ss.bg, color: ss.color,
                            textTransform: 'capitalize',
                          }}>{p.status}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 50, height: 5, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.min(c, 100)}%`, height: '100%', borderRadius: 3,
                                background: c >= 80 ? '#22c55e' : c >= 60 ? '#f59e0b' : '#ef233c',
                              }} />
                            </div>
                            <span style={{
                              fontSize: 12, fontWeight: 700,
                              color: c >= 80 ? '#16a34a' : c >= 60 ? '#d97706' : '#dc2626',
                            }}>{c}%</span>
                          </div>
                        </td>
                        <td>
                          <Link to={`/patrols/${p.id}`} className="btn btn-sm btn-primary">View</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // ADMIN / SUPERVISOR VIEW
  // ─────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🚶 Patrols</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            {patrols.length} total · {patrols.filter(p => p.status === 'active').length} active now
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
          {['all', 'active', 'completed', 'abandoned'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: '5px 14px', borderRadius: 7, border: 'none',
              background: filter === s ? 'white' : 'transparent',
              color: filter === s ? '#1e293b' : '#64748b',
              fontWeight: filter === s ? 700 : 400,
              fontSize: 12, cursor: 'pointer', transition: 'all .15s',
              boxShadow: filter === s ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
              textTransform: 'capitalize',
            }}>{s}</button>
          ))}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
            No {filter !== 'all' ? filter : ''} patrols found
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Guard</th><th>Route</th><th>Site</th>
                  <th>Started</th><th>Status</th>
                  <th>CPs</th><th>Compliance</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const ss = statusStyle[p.status] || { bg: '#f3f4f6', color: '#374151' };
                  const c  = parseFloat(p.compliance_percent) || 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                            background: 'linear-gradient(135deg,#4361ee,#8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13,
                          }}>👮</div>
                          <span style={{ fontWeight: 600 }}>{p.guard_name}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: 500 }}>{p.route_name}</td>
                      <td style={{ color: '#64748b' }}>{p.site_name}</td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>
                        {fmtDateShort(p.started_at)}
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '3px 10px', borderRadius: 20,
                          fontWeight: 700, background: ss.bg, color: ss.color,
                          textTransform: 'capitalize',
                        }}>{p.status}</span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        <span style={{ fontWeight: 600 }}>{p.completed_checkpoints}</span>
                        <span style={{ color: '#94a3b8' }}>/{p.total_checkpoints}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 50, height: 5, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                            <div style={{
                              width: `${Math.min(c, 100)}%`, height: '100%', borderRadius: 3,
                              background: c >= 80 ? '#22c55e' : c >= 60 ? '#f59e0b' : '#ef233c',
                            }} />
                          </div>
                          <span style={{
                            fontSize: 12, fontWeight: 700,
                            color: c >= 80 ? '#16a34a' : c >= 60 ? '#d97706' : '#dc2626',
                          }}>{c}%</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link to={`/patrols/${p.id}`} className="btn btn-sm btn-primary">View</Link>
                          {isAdmin && p.status === 'active' && (
                            <button
                              onClick={() => handleStop(p.id)}
                              className="btn btn-sm btn-danger"
                            >🛑 Stop</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
